import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Baby, Calendar, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

/**
 * Iter60.p65 — Breastfeeding Period card on Employee Profile.
 *
 * Backed by the dedicated `PATCH /api/employees/{id}/breastfeeding-period`
 * endpoint (Admin + HR write, everyone reads). Rejects male employees
 * server-side, so we short-circuit on `gender !== female` in the UI too.
 *
 * The chip in the profile header — activated period + auto-computed
 * end date — makes it obvious to attendance officers that a 2-hour
 * daily worked-time credit is in effect for this employee.
 */
export default function BreastfeedingPeriodCard({ employee, canEdit = false }) {
    const [bf, setBf] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [evidenceId, setEvidenceId] = useState("");

    const reload = async () => {
        if (!employee?.id) return;
        setLoading(true);
        try {
            const { data } = await api.get(
                `/employees/${employee.id}/breastfeeding-period`,
            );
            setBf(data);
            setStartDate(data?.start_date || "");
            setEvidenceId(data?.birth_evidence_attachment_id || "");
        } catch {
            setBf(null);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { reload(); }, [employee?.id]);

    if ((employee?.gender || "").toLowerCase() !== "female") return null;

    const activate = async () => {
        if (!startDate) {
            toast.error("Pick a start date first.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch(
                `/employees/${employee.id}/breastfeeding-period`,
                {
                    start_date: startDate,
                    birth_evidence_attachment_id: evidenceId || null,
                    active: true,
                },
            );
            setBf(data);
            toast.success("Breastfeeding period activated.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const deactivate = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(
                `/employees/${employee.id}/breastfeeding-period`,
                { active: false, start_date: bf?.start_date },
            );
            setBf(data);
            toast.success("Breastfeeding period deactivated.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return null;

    const active = !!bf?.active;

    return (
        <div className="asl-card p-4 space-y-3" data-testid="breastfeeding-period-card">
            <div className="flex items-center gap-2">
                <Baby className="h-5 w-5 asl-crimson-text" />
                <div>
                    <h3 className="text-sm font-bold asl-navy-text">
                        Breastfeeding period (Article 130)
                    </h3>
                    <p className="text-xs text-[color:var(--asl-muted)]">
                        2 hours per working day of deemed-worked time for 12 months post-birth.
                    </p>
                </div>
            </div>
            {active ? (
                <div
                    className="rounded-md border border-[color:var(--asl-crimson)] bg-[color:var(--asl-crimson-50,#FDF2F5)] p-3 text-xs space-y-1"
                    data-testid="breastfeeding-active-block"
                >
                    <div className="asl-crimson-text font-semibold">
                        Active — 2 hrs/day worked-time credit
                    </div>
                    <div className="text-[color:var(--asl-muted)]">
                        Start <span className="mono">{bf.start_date}</span> · Ends{" "}
                        <span className="mono">{bf.end_date}</span>
                        {bf.auto_expired && (
                            <span className="ml-2 asl-crimson-text italic">
                                (auto-expired — deactivate to clean up)
                            </span>
                        )}
                    </div>
                    {canEdit && (
                        <Button
                            variant="outline"
                            onClick={deactivate}
                            disabled={saving}
                            data-testid="breastfeeding-deactivate-btn"
                            className="mt-2 border-[color:var(--asl-crimson)] asl-crimson-text"
                        >
                            {saving ? "Working…" : "Deactivate"}
                        </Button>
                    )}
                </div>
            ) : (
                <div className="text-xs text-[color:var(--asl-muted)]" data-testid="breastfeeding-inactive-block">
                    Not active. Activate when HR has confirmed the birth.
                </div>
            )}
            {canEdit && !active && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs uppercase">Start date</Label>
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            data-testid="breastfeeding-start-date"
                        />
                        <p className="text-[10px] text-[color:var(--asl-muted)] mt-1">
                            End date auto-computed as start + 365 days.
                        </p>
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Birth evidence attachment id</Label>
                        <Input
                            placeholder="attachment id or URL"
                            value={evidenceId}
                            onChange={(e) => setEvidenceId(e.target.value)}
                            data-testid="breastfeeding-evidence-id"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <Button
                            onClick={activate}
                            disabled={saving || !startDate}
                            className="asl-crimson-bg text-white hover:opacity-90"
                            data-testid="breastfeeding-activate-btn"
                        >
                            <ShieldCheck className="h-4 w-4 mr-1" />
                            {saving ? "Activating…" : "Activate breastfeeding period"}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}


/** Compact chip for the profile header showing an active period at a glance. */
export function BreastfeedingChip({ employee }) {
    const bf = employee?.breastfeeding_period;
    if (!bf?.active) return null;
    return (
        <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
            style={{
                background: "rgba(168, 50, 74, 0.12)",
                color: "var(--asl-crimson)",
                border: "1px solid rgba(168, 50, 74, 0.25)",
            }}
            title={`Article 130 — 2 hrs/day worked-time credit. Ends ${bf.end_date}`}
            data-testid="breastfeeding-chip"
        >
            <Baby className="h-3 w-3" />
            Breastfeeding — ends {bf.end_date}
        </span>
    );
}
